import { Box } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import * as calendarTaskService from "source/models/services/calendarTaskService";
import { ISessionState } from "source/models/session/sessionState";
import { IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { CalendarTaskRepetitionType, CalendarTaskState, ConfirmationDialogResult, DatabaseRecordType } from "ui/Scripts/source/models/enums";
import { Translations } from "ui/Scripts/source/models/translations";
import { IModifyUser } from "ui/Scripts/source/models/user/modifyUser";
import { Dialog } from "../../framework/Dialog/Dialog";
import { DialogButtons } from "../../framework/Dialog/DialogButtons";
import { DialogItemsNav } from "../../framework/Dialog/DialogItemsNav";
import { DialogTabs } from "../../framework/Dialog/DialogTabs";
import { CalendarTaskBasicInformation } from "./CalendarTaskBasicInformation";
import { CalendarTaskRepetitionSettings, IRepetitionIntervalYear } from "./CalendarTaskRepetitionSettings";
import { Base } from "ui/Scripts/source/framework/base";
import { LockedRecord } from "ui/Scripts/source/models/common/lockedRecord";
import { recordLockManager } from "source/models/services/recordLockService";
import { AppUtils } from "source/framework/appUtils";
import { ICalendarTaskCategoryListItem } from "ui/Scripts/source/models/calendarTaskCategory/calendarTaskCategoryListItem";
import { ICalendarTaskEditItem } from "ui/Scripts/source/models/calendarTask/calendarTaskEditItem";
import { SaveDataObj } from "source/framework/saveData";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { removeConfirmationWord } from "ui/Scripts/source/models/consts";
import { StoreUtils } from "source/models/store/storeUtils";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";

interface ICalendarTaskEditDialogProps {
    sessionState: ISessionState;
    isReadOnly: boolean;
    companyId: string;
    calendarTaskId: string;
    databaseRecordType: DatabaseRecordType;
    recordId: string;
    ownerRecordTitle?: string;
    startDate: number;
    hasPrevious: boolean;
    hasNext: boolean;
    onRefreshCalendar: () => Promise<void>;
    onSaved: (id: string) => void;
    onClose: () => void;
    onNewSaved: (id: string) => void;
    onPrevious: () => void;
    onNext: () => void;
}

/* eslint-disable no-unused-vars */
export const enum CalendarTaskDialogMenuItem {
  General = "1",
  Repetition = "2",
}
/* eslint-enable no-unused-vars */

interface ICalendarTaskEditDialogState {
    isLoading: boolean;
    calendarTask: ICalendarTaskEditItem;
    categories: ICalendarTaskCategoryListItem[];

    id: string;
    parentId: string;
    rowId: string;
    categoryId: string;
    title: string;
    locationText: string;
    state: CalendarTaskState;
    description: string;
    startDateStr: string;
    startTimeStr: string;
    endDateStr: string;
    endTimeStr: string;
    allDay: boolean;
    isPrivate: boolean;
    remainder: string;
    repetitionIntervalType: CalendarTaskRepetitionType;
    repetitionInterval: number;
    repetitionIntervalWeek: number[];
    repetitionIntervalMonth: number;
    repetitionIntervalYear: IRepetitionIntervalYear;
    repetitionEndRepeatCount: number;
    repetitionEndDateStr: string;
    attachments: IFileAttachment[];
    handlers: IModifyUser[];
    canSaveCalendarTask: boolean;
    canRemoveCalendarTask: boolean;
    canShareCalendarTasks: boolean;

    isNew: boolean;
    lockedUser: IModifyUser;
    isLockedToOtherUser: boolean;

    activeMenuItem: CalendarTaskDialogMenuItem;
}

export const CalendarTaskEditDialog = (props: ICalendarTaskEditDialogProps) => {
    const orgStateHash = useRef("");
    
    const [state, setState] = useState<ICalendarTaskEditDialogState>({
        isLoading: false,
        calendarTask: null,
        categories: [],
        id: "",
        parentId: "",
        rowId: "",
        categoryId: "",
        title: "",
        locationText: "",
        startDateStr: "",
        startTimeStr: "",
        endDateStr: "",
        endTimeStr: "",
        allDay: false,
        description: "",
        isPrivate: false,
        state: CalendarTaskState.NotStarted,
        remainder: "",
        repetitionIntervalType: CalendarTaskRepetitionType.NotRepeated,
        repetitionInterval: 1,
        repetitionIntervalWeek: [0],
        repetitionIntervalMonth: 1,
        repetitionIntervalYear: { month: 1, day: 1 },
        repetitionEndRepeatCount: 1,
        repetitionEndDateStr: "",
        attachments: [],
        handlers: [],
        activeMenuItem: CalendarTaskDialogMenuItem.General,
        canSaveCalendarTask: false,
        canRemoveCalendarTask: false,
        canShareCalendarTasks: false,
        isNew: false,
        lockedUser: null,
        isLockedToOtherUser: false
    });

    const getRepetitionIntervalWeek = (repetitionIntervalType: CalendarTaskRepetitionType, repetitionIntervalSub: number, repetitionStartDate: number): number[] => {
        if (repetitionIntervalType !== CalendarTaskRepetitionType.Weekly) {
            return [((props.startDate ? (new Date(props.startDate))?.getDay() : (new Date(repetitionStartDate))?.getDay()) + 6) % 7];
        }
        const result: number[] = [];
        const str = Base.padZeros(repetitionIntervalSub.toString(10), 7);
        for (let i = 0; i < str.length; i++) {
            if (str[i] === "0") continue;
            result.push(i);
        }
        return result.length ? result : [0];
    };

    const getRepetitionIntervaMonth = (repetitionIntervalType: CalendarTaskRepetitionType, repetitionIntervalSub: number): number => {
        if (repetitionIntervalType !== CalendarTaskRepetitionType.Monthly) return (new Date()).getDate();
        return repetitionIntervalSub > 0 && repetitionIntervalSub < 32 ? repetitionIntervalSub : 1;
    };

    const getRepetitionIntervalYear = (repetitionIntervalType: CalendarTaskRepetitionType, repetitionIntervalSub: number): IRepetitionIntervalYear => {
        const nowDate = new Date();
        const result: IRepetitionIntervalYear = { month: nowDate.getMonth() + 1, day: nowDate.getDate() };
        if (repetitionIntervalType !== CalendarTaskRepetitionType.Yearly) return result;
        const str = Base.padZeros(repetitionIntervalSub.toString(10), 4);
        const parts = Base.getValidDateParts(str.substring(2).toInteger(), str.substring(0, 2).toInteger(), null);
        result.day = parts.day;
        result.month = parts.month;
        return result;
    };

    const getRepetitionIntervalSub = (state: ICalendarTaskEditDialogState): number => {
        if (state.repetitionIntervalType === CalendarTaskRepetitionType.Weekly) {
            let value = 0;
            for (let i = 0; i < state.repetitionIntervalWeek.length; i++) {
                value = value + Math.pow(10, 6 - state.repetitionIntervalWeek[i]);
            }
            return value;
        } else if (state.repetitionIntervalType === CalendarTaskRepetitionType.Monthly) {
            return state.repetitionIntervalMonth;
        } else if (state.repetitionIntervalType === CalendarTaskRepetitionType.Yearly) {
            return state.repetitionIntervalYear.month * 100 + state.repetitionIntervalYear.day;
        }
        return 0;
    };

    const getSaveDataFromState = async (saveState: ICalendarTaskEditDialogState): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("rowId", saveState.rowId);
        result.append("companyId", props.companyId);
        result.append("id", saveState.id);
        result.append("categoryId", saveState.categoryId ? saveState.categoryId : null);
        result.append("recordId", props.recordId);
        result.append("databaseRecordType", props.databaseRecordType);
        result.append("title", saveState.title);
        result.append("locationText", saveState.locationText);
        result.append("description", saveState.description);
        result.append("startDate", (Base.getDateTime(saveState.startDateStr, saveState.startTimeStr) ?? new Date()).getTime());
        result.append("endDate", Base.getDateTime(saveState.endDateStr, saveState.endTimeStr)?.getTime());
        result.append("allDay", saveState.allDay);
        result.append("isPrivate", saveState.isPrivate);
        result.append("state", saveState.state);
        result.append("remainder", saveState.remainder ? saveState.remainder.toInteger() : -1);
        result.append("repetitionIntervalType", saveState.repetitionIntervalType);
        result.append("repetitionInterval", saveState.repetitionInterval);
        result.append("repetitionIntervalSub", getRepetitionIntervalSub(saveState));
        result.append("repetitionEndRepeatCount", saveState.repetitionEndDateStr ? null : saveState.repetitionEndRepeatCount);
        result.append("repetitionEndDate", saveState.repetitionEndRepeatCount ? null : saveState.repetitionEndDateStr.toUtcDate().getTime());
        result.append("attachments", saveState.isNew
            ? await SaveAttachmentBase64.fromFileAttachments(saveState.attachments)
            : null
        );
        result.append("addHandlers", saveState.isNew
            ? saveState.handlers.map(i => { return { code: i.code, type: i.date }; })
            : null
        );
        return result;
    };

    const loadCalendarTask = async (id: string) => {
        //Unlock current calendar task
        if (LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
            await recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.CalendarTask);
        }
        //Load and lock calendar task
        setState((old) => ({
            ...old,
            isLoading: true
        }));
        const result = await AppUtils.callService(() => calendarTaskService.getCalendarTaskEdit(props.companyId, id, props.recordId, props.databaseRecordType), false, false, () => setState((old) => ({ ...old, isLoading: false })));
        if (!result) return;
        const calendarTask = result.item;
        const isNew = calendarTask.id === Base.emptyGuid;
        const newState = {
            ...state,
            calendarTask,
            categories: result.categories,
            id: calendarTask.id,
            parentId: calendarTask.parentId,
            categoryId: calendarTask.categoryId,
            rowId: calendarTask.rowId,
            state: calendarTask.state,
            title: calendarTask.title,
            locationText: calendarTask.locationText,
            description: calendarTask.description,
            startDateStr: isNew && props.startDate
                ? Base.timeToDateStr(props.startDate)
                : Base.timeToDateStr(calendarTask.startDate),
            startTimeStr: Base.timeToTimeStr(calendarTask.startDate),
            endDateStr: calendarTask.endDate
                ? Base.timeToDateStr(calendarTask.endDate)
                : "",
            endTimeStr: calendarTask.endDate
                ? Base.timeToTimeStr(calendarTask.endDate)
                : "",
            allDay: calendarTask.allDay,
            isPrivate: calendarTask.isPrivate,
            remainder: calendarTask.remainder >= 0
                ? calendarTask.remainder.toString(10)
                : "",
            repetitionIntervalType: calendarTask.repetitionIntervalType,
            repetitionInterval: calendarTask.repetitionInterval,
            repetitionIntervalWeek: getRepetitionIntervalWeek(calendarTask.repetitionIntervalType, calendarTask.repetitionIntervalSub, calendarTask.startDate),
            repetitionIntervalMonth: getRepetitionIntervaMonth(calendarTask.repetitionIntervalType, calendarTask.repetitionIntervalSub),
            repetitionIntervalYear: getRepetitionIntervalYear(calendarTask.repetitionIntervalType, calendarTask.repetitionIntervalSub),
            repetitionEndRepeatCount: calendarTask.repetitionEndRepeatCount,
            repetitionEndDateStr: calendarTask.repetitionEndDate
                ? Base.utcTimeToDateStr(calendarTask.repetitionEndDate)
                : "",
            attachments: calendarTask.attachments,
            handlers: calendarTask.handlers,
            canSaveCalendarTask: result.canSaveCalendarTask,
            canRemoveCalendarTask: result.canRemoveCalendarTask,
            canShareCalendarTasks: result.canShareCalendarTasks,
            isNew,
            lockedUser: calendarTask.lockedUser,
            isLockedToOtherUser: calendarTask.isLockedToOtherUser(props.sessionState.code),
            //showNewTaskDialog: isNew,
            activeMenuItem: isNew
                ? CalendarTaskDialogMenuItem.General
                : state.activeMenuItem
        };
        setState((old) => ({
            ...old,
            ...newState,
        }));
        orgStateHash.current = (await getSaveDataFromState(newState)).hash;
        //Add locked record to idb
        if (!isNew && LockedRecord.isLockedToUser(calendarTask.lockedUser, props.sessionState.code)) {
            recordLockManager.addLock(LockedRecord.createLockedRecord(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.CalendarTask));
        }
        //Send read receipt for calendar task
        // if (!isNew) {
        //     const userIsCreator = calendarTask.createUser.code === props.sessionState.code;
        //     if (!userIsCreator && !calendarTask.isRead) {
        //         AppUtils.callService(() => calendarTaskService.sendTaskReadReceipt(props.companyId, state.id), false, true);
        //     }
        // }
    };

    const checkErrors = (): Promise<boolean> => {
        return new Promise<boolean>((resolve) => {
            if (!state.title) {
                AppUtils.showErrorMessage(Translations.TitleMustBeDefined);
                return resolve(false);
            }
            if (!state.startDateStr) {
                AppUtils.showErrorMessage(Translations.CalendarTaskStartDateMustBeDefined);
                return resolve(false);
            }
            if (state.endDateStr) {
                const startDateTime = Base.getDateTime(state.startDateStr, state.startTimeStr);
                const endDateTime = Base.getDateTime(state.endDateStr, state.endTimeStr);
                if (startDateTime.getTime() > endDateTime.getTime()) {
                    AppUtils.showErrorMessage(Translations.CalendarTaskEndDateMustBeAfterStartDate);
                    return resolve(false);
                }
            }
            return resolve(true);
        });
    };

    const saveToDb = async (): Promise<SaveSuccess> => {
        if (!await AppUtils.validate(checkErrors)) return null;
        const saveData = await getSaveDataFromState(state);
        //Call server
        const result = await AppUtils.callService(() => calendarTaskService.saveCalendarTaskEdit(saveData.obj));
        if (!result) return null;
        const stateChange = { id: result.id, rowId: result.rowId, isNew: false };
        setState((old) => ({
            ...old,
            ...stateChange,
        }));
        orgStateHash.current = (await getSaveDataFromState({ ...state, ...stateChange })).hash;
        return result;
    };

    const componentWillUnmount = useRef(false);

    useEffect(() => {
        return () => {
            componentWillUnmount.current = true;
        };
    }, []);

    useEffect(() => {
        const loadData = async () => {
            if (orgStateHash.current) {
                const cancelResult = await AppUtils.cancel((await getSaveDataFromState(state)).hash, orgStateHash.current, null);
                if (cancelResult === ConfirmationDialogResult.Cancel) return;
                if (cancelResult === ConfirmationDialogResult.Yes) {
                    if (!await saveToDb()) return;
                    await props.onRefreshCalendar();
                }
            }
            await loadCalendarTask(props.calendarTaskId);
        };

        loadData();
    }, [props.calendarTaskId]);

    useEffect(() => {
        const unlockRecord = async () => {
            if (!state.isNew && LockedRecord.isLockedToUser(state.lockedUser, props.sessionState.code)) {
                recordLockManager.unlock(props.companyId, state.id, DatabaseRecordType.CalendarTask);
            }
        };

        return () => {
            if (componentWillUnmount.current) {
                unlockRecord();
            }
        };
    }, [state.id]);

    const handleSave = async (closeDialog: boolean) => {
        const result = await saveToDb();
        if (!result) return false;
        if (!state.isNew) {
            if (!closeDialog) {
                await loadCalendarTask(result.id);
                props.onSaved(result.id);
                return;
            }
            props.onClose();
        } else {
            props.onNewSaved(result.id);
        }
        return true;
    };

    const handleCancel = async () => {
        const cancelResult = await AppUtils.cancel((await getSaveDataFromState(state)).hash, orgStateHash.current);
        if (cancelResult === ConfirmationDialogResult.Cancel) return;
        if (cancelResult === ConfirmationDialogResult.Yes) {
            await handleSave(true);
        } else {
            props.onClose();
        }
    };

    const handleRemove = async () => {
        if (state.isLockedToOtherUser || state.isNew) return;
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToRemoveCalendarTaskParameter, state.calendarTask.title), false, removeConfirmationWord) === ConfirmationDialogResult.Yes) {
            const result = await AppUtils.callService(() => calendarTaskService.removeCalendarTask(props.companyId, state.calendarTask.id));
            if (!result) return;
            setState((old) => ({
                ...old,
                lockedUser: null
            }));
            props.onClose();
        }
    };

    const canMoveOut = async (): Promise<boolean> => {
        return await AppUtils.canMoveOut((await getSaveDataFromState(state)).hash, orgStateHash.current,
            async () => !!(await saveToDb()),
            props.onRefreshCalendar,
            (newStateHash: string) => { orgStateHash.current = newStateHash; }
        );
    };

    const handlePrevious = async () => {
        if (!await canMoveOut()) return;
        props.onPrevious();
    };

    const handleNext = async () => {
        if (!await canMoveOut()) return;
        props.onNext();
    };

    const handleToggleActiveMenuItem = (tabId: CalendarTaskDialogMenuItem) => {
        setState((old) => ({
            ...old,
            activeMenuItem: tabId
        }));
    };

    // #region Locking
    const handleUnlockAndLockToMe = async () => {
        if (await AppUtils.showConfirmationDialog(String.format(Translations.DoYouReallyWantToUnlockCalendarTask, state.calendarTask.title), false) !== ConfirmationDialogResult.Yes) {
            return;
        }
        const result = await recordLockManager.lock(props.companyId, state.id, props.sessionState.code, DatabaseRecordType.CalendarTask);
        if (!result || result.items.length < 1) return;
        setState((old) => ({
            ...old,
            lockedUser: StoreUtils.getNowModifyUser(result.items[0].lockedDate),
            isLockedToOtherUser: false
        }));
    };
    // #endregion Locking

    const handleChange = (name: string, value: string | number | boolean | IRepetitionIntervalYear | number[]) => {
        const stateChange = { [name]: value };
        if (name === "allDay" && value) {
            stateChange.startTimeStr = "0:00";
            stateChange.endTimeStr = "0:00";
        }
        setState((old) => ({
            ...old,
            ...stateChange,
        }));
    };

    const handleChangeHandlers = (handlers: IModifyUser[]) => {
        setState((old) => ({
            ...old,
            handlers
        }));
    };

    const handleChangeAttachments = (attachments: IFileAttachment[]) => {
        setState((old) => ({
            ...old,
            attachments
        }));
    };

    const getRecurrenceTypeString = (): string => {
        switch (state.repetitionIntervalType) {
        case CalendarTaskRepetitionType.Weekly:
            return Translations.Weekly;
        case CalendarTaskRepetitionType.Monthly:
            return Translations.Monthly;
        case CalendarTaskRepetitionType.Yearly:
            return Translations.Yearly;
        default:
            return Translations.NotRepeated;
        }
    };

    if (!state.calendarTask) return null;

    const isReadOnly = props.isReadOnly || state.isLockedToOtherUser || !state.canSaveCalendarTask;

    return (
        <Dialog
            id="calendarTaskDialog"
            maxWidth="lg"
            title={state.isNew
                ? Translations.AddCalendarTask
                : ((state.calendarTask.parentId
                    ? (state.canSaveCalendarTask ? Translations.CalendarTaskEditOccurrence : Translations.CalendarTaskOpenOccurrence)
                    : (state.calendarTask.hasChildren
                        ? (state.canSaveCalendarTask ? Translations.CalendarTaskEditSeries : Translations.CalendarTaskOpenSeries)
                        : (state.canSaveCalendarTask ? Translations.EditCalendarTask : Translations.OpenCalendarTask))) + ": " + state.calendarTask.title +
                        (state.isLockedToOtherUser ? ` (${Translations.Locked})` : "") + (state.isPrivate ? ` (${Translations.Private})` : ""))}
            maxHeight="802px"
            dialogTabs={
                <DialogTabs
                    activeTabId={state.activeMenuItem}
                    tabs={[
                        { id: CalendarTaskDialogMenuItem.General, title: Translations.CalendarTask },
                        { id: CalendarTaskDialogMenuItem.Repetition, title: Translations.Recurrence + ` (${getRecurrenceTypeString().toLocaleLowerCase()})`, hidden: !!state.parentId }
                    ]}
                    onChangeTab={handleToggleActiveMenuItem}
                />
            }
            body={
                <>
                    {state.activeMenuItem === CalendarTaskDialogMenuItem.General && (
                        <CalendarTaskBasicInformation
                            sessionState={props.sessionState}
                            categories={state.categories}
                            isReadOnly={isReadOnly}
                            canShareCalendarTasks={state.canShareCalendarTasks}
                            companyId={props.companyId}
                            isNew={state.isNew}
                            lockedUser={state.lockedUser}
                            id={state.id}
                            title={state.title}
                            locationText={props.ownerRecordTitle ?? state.locationText}
                            recordId={state.calendarTask.recordId}
                            databaseRecordType={state.calendarTask.databaseRecordType}
                            startDateStr={state.startDateStr}
                            startTimeStr={state.startTimeStr}
                            endDateStr={state.endDateStr}
                            endTimeStr={state.endTimeStr}
                            allDay={state.allDay}
                            description={state.description}
                            isPrivate={state.isPrivate}
                            state={state.state}
                            categoryId={state.categoryId}
                            remainder={state.remainder}
                            attachments={state.attachments}
                            handlers={state.handlers}
                            onChange={handleChange}
                            onChangeHandlers={handleChangeHandlers}
                            onChangeAttachments={handleChangeAttachments}
                            onUnlockAndLockToMe={handleUnlockAndLockToMe}
                        />
                    )}
                    {state.activeMenuItem === CalendarTaskDialogMenuItem.Repetition && (
                        <CalendarTaskRepetitionSettings
                            orgStartDate={props.startDate}
                            startDateStr={state.startDateStr}
                            isReadOnly={isReadOnly}
                            repetitionIntervalType={state.repetitionIntervalType}
                            repetitionInterval={state.repetitionInterval}
                            repetitionIntervalWeek={state.repetitionIntervalWeek}
                            repetitionIntervalMonth={state.repetitionIntervalMonth}
                            repetitionIntervalYear={state.repetitionIntervalYear}
                            repetitionEndRepeatCount={state.repetitionEndRepeatCount}
                            repetitionEndDateStr={state.repetitionEndDateStr}
                            onChange={handleChange}
                        />
                    )}
                </>
            }
            actions={
                <Box display="flex" justifyContent="space-between" width="100%">
                    <DialogItemsNav
                        hasPrevious={props.hasPrevious}
                        hasNext={props.hasNext}
                        onPrevious={handlePrevious}
                        onNext={handleNext}
                        createUser={!state.isNew ? state.calendarTask?.createUser : null}
                        modifyUser={!state.isNew ? state.calendarTask?.modifyUser : null}
                        lockedUser={state.lockedUser}
                        isLockedToOtherUser={!state.isNew && state.isLockedToOtherUser}
                        onUnlockAndLockToMe={props.sessionState.isSuperAdmin ? handleUnlockAndLockToMe : null}
                    />
                    <DialogButtons
                        isNew={state.isNew}
                        removeDisabled={isReadOnly}
                        saveDisabled={isReadOnly}
                        onCancel={handleCancel}
                        onRemove={!state.isNew ? handleRemove : null}
                        onSave={() => handleSave(false)}
                    />
                </Box>
            }
            onClose={handleCancel}
        />
    );
};
